import React, { useState } from 'react';
import {
  View,
  ScrollView,
  TextInput,
  Alert,
  StyleSheet,
} from 'react-native';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';

import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AppButton from '../components/AppButton';
import Util from '../util/util.js';
import { setOrganizationAccess } from '../util/storage';
import { useFormValidation, validateRequired } from '../util/validation';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { t as translate, useTranslation } from '../i18n';

// Same format rule as the backend
const ORG_NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_-]{1,62}[a-zA-Z0-9]$/;

const validateOrgName = (value) => {
  const requiredError = validateRequired(value, translate('admin:createParentOrg.nameLabel'));
  if (requiredError) return requiredError;
  const name = value.trim();
  if (name.length < 3 || !ORG_NAME_REGEX.test(name)) {
    return translate('admin:createParentOrg.nameInvalid');
  }
  return null;
};

const CreateParentOrg = ({ navigation, route }) => {
  const { t } = useTranslation('admin');

  const { containerStyle } = useResponsiveStyles();
  const { organization, admin, userAdmin, orgAdmin } = route.params || {};

  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(false);

  const { values, setDisplayErrors, handleChange, validate, getFieldError } = useFormValidation(
    { orgName: '', emailDomains: '' },
    { orgName: [validateOrgName] },
  );
  const { orgName, emailDomains } = values;

  const handleSubmit = async () => {
    setDisplayErrors(true);
    if (!validate().isValid) return;

    try {
      setLoading(true);

      // Parse email domains
      const domainList = emailDomains
        .split(',')
        .map((d) => d.trim())
        .filter((d) => d.length > 0);

      const response = await Util.post('Resty', '/items/organization/parent', {
        body: {
          name: orgName.trim(),
          allowed_email_domains: domainList,
        },
      });

      if (response && response.success) {
        // The backend granted the creator access to the new org; refresh the locally-cached
        // organization_access so it shows up immediately in User Management / Org Settings.
        try {
          const rolesResponse = await Util.get('Resty', '/items/getUserRoles');
          if (rolesResponse.data?.organization_access) {
            await setOrganizationAccess(rolesResponse.data.organization_access);
          }
        } catch (refreshErr) {
          console.log('Could not refresh org access after create:', refreshErr);
        }

        setCreated(true);
        setTimeout(() => {
          navigation.goBack();
        }, 1500);
      } else {
        Alert.alert(t('common:actions.error'), response.error || t('createParentOrg.createFailed'));
      }
    } catch (error) {
      console.log('Error creating organization:', error);
      Alert.alert(t('common:actions.error'), t('createParentOrg.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={[containerStyle, styles.container]}>
      <Header organization={organization} admin={admin} userAdmin={userAdmin} type="pop" />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.contentWrapper}>
          {/* Page Header */}
          <View style={styles.pageHeader}>
            <CustomText font="semiBold" size={24} color={AppStyles.colorSet.textPrimary} style={styles.centeredText}>
              {t('createParentOrg.title')}
            </CustomText>
            <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary} style={[styles.pageSubtitle, styles.centeredText]}>
              {t('createParentOrg.subtitle')}
            </CustomText>
          </View>

          {/* Form Card */}
          <View style={styles.formCard}>
            {/* Organization Name Field */}
            <View style={styles.fieldGroup}>
              <CustomText font="medium" size={15} color={AppStyles.colorSet.textPrimary}>
                {t('createParentOrg.nameLabel')} <CustomText color={AppStyles.colorSet.error}>*</CustomText>
              </CustomText>
              <CustomText font="regular" size={13} color={AppStyles.colorSet.textSecondary} style={styles.fieldHint}>
                {t('createParentOrg.nameHint')}
              </CustomText>
              <TextInput
                style={[styles.input, getFieldError('orgName') && styles.inputError]}
                value={orgName}
                onChangeText={handleChange('orgName')}
                placeholder={t('createParentOrg.namePlaceholder')}
                placeholderTextColor={AppStyles.colorSet.textTertiary}
                autoCapitalize="none"
                autoCorrect={false}
              />
              {getFieldError('orgName') && (
                <CustomText font="regular" size={13} color={AppStyles.colorSet.error} style={styles.fieldError}>
                  {getFieldError('orgName')}
                </CustomText>
              )}
            </View>

            {/* Email Domains Field */}
            <View style={styles.fieldGroup}>
              <CustomText font="medium" size={15} color={AppStyles.colorSet.textPrimary}>
                {t('createParentOrg.domainsLabel')}
              </CustomText>
              <CustomText font="regular" size={13} color={AppStyles.colorSet.textSecondary} style={styles.fieldHint}>
                {t('createParentOrg.domainsHint')}
              </CustomText>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={emailDomains}
                onChangeText={handleChange('emailDomains')}
                placeholder={t('createParentOrg.domainsPlaceholder')}
                placeholderTextColor={AppStyles.colorSet.textTertiary}
                autoCapitalize="none"
                autoCorrect={false}
                multiline
              />
            </View>
          </View>

          {/* Info Box */}
          <View style={styles.infoBox}>
            <View style={styles.infoHeader}>
              <CustomText style={styles.infoIcon}>💡</CustomText>
              <CustomText font="semiBold" size={15} color={AppStyles.colorSet.info}>
                {t('createParentOrg.whatNext')}
              </CustomText>
            </View>
            <View style={styles.infoList}>
              <CustomText font="regular" size={14} color={AppStyles.colorSet.textPrimary} style={styles.infoItem}>
                {t('createParentOrg.nextGroup')}
              </CustomText>
              <CustomText font="regular" size={14} color={AppStyles.colorSet.textPrimary} style={styles.infoItem}>
                {t('createParentOrg.nextUsers')}
              </CustomText>
              <CustomText font="regular" size={14} color={AppStyles.colorSet.textPrimary} style={styles.infoItem}>
                {t('createParentOrg.nextChildren')}
              </CustomText>
              <CustomText font="regular" size={14} color={AppStyles.colorSet.textPrimary} style={styles.infoItem}>
                {t('createParentOrg.nextConfigure')}
              </CustomText>
            </View>
          </View>

          {/* Submit Button */}
          <View style={styles.buttonContainer}>
            {created ? (
              <View style={styles.successMessage}>
                <CustomText font="semiBold" size={18} color={AppStyles.colorSet.success}>
                  {t('createParentOrg.created')}
                </CustomText>
              </View>
            ) : (
              <AppButton
                title={t('createParentOrg.submit')}
                onPress={handleSubmit}
                isBusy={loading}
                isEnabled={!loading}
                fullWidth
              />
            )}
          </View>
        </View>
      </ScrollView>
      <Footer
        organization={organization}
        admin={admin}
        userAdmin={userAdmin}
        orgAdmin={orgAdmin}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    paddingBottom: 40,
    flexGrow: 1,
    alignItems: 'center',
  },
  contentWrapper: {
    width: '100%',
    maxWidth: 600,
    paddingHorizontal: 16,
  },
  pageHeader: {
    paddingTop: hp(3),
    paddingBottom: hp(2),
    alignItems: 'center',
  },
  pageSubtitle: {
    marginTop: 8,
  },
  centeredText: {
    textAlign: 'center',
  },
  formCard: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    marginBottom: 20,
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  fieldGroup: {
    marginBottom: 24,
  },
  fieldHint: {
    marginTop: 4,
    marginBottom: 12,
  },
  input: {
    backgroundColor: '#F9FAFB',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    fontFamily: AppStyles.fontSet.regular,
    color: AppStyles.colorSet.textPrimary,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  inputError: {
    borderColor: AppStyles.colorSet.error,
  },
  fieldError: {
    marginTop: 6,
  },
  infoBox: {
    backgroundColor: AppStyles.colorSet.infoLight,
    marginBottom: 24,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.2)',
  },
  infoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  infoIcon: {
    fontSize: 18,
    marginRight: 8,
  },
  infoList: {
    paddingLeft: 4,
  },
  infoItem: {
    lineHeight: 24,
  },
  buttonContainer: {
    width: '100%',
  },
  successMessage: {
    backgroundColor: AppStyles.colorSet.successLight,
    borderRadius: 12,
    paddingVertical: 18,
    paddingHorizontal: 24,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: AppStyles.colorSet.success,
  },
});

export default CreateParentOrg;
